/** @jsx jsx */
import React from "react"
import { graphql } from "gatsby"
import { jsx, Box, Flex, Heading, Paragraph } from "theme-ui"
import { GatsbyImage } from "gatsby-plugin-image"
import Action from "../../shared/Action"
import "./gridsection.css"
import ContentfulRichText from "../../shared/ContentfulRichText"

export const query = graphql`
  fragment GridItemFragment on ContentfulGridItem {
    action {
      label
      style
      url
      icon
    }
    image {
      gatsbyImageData
      file {
        contentType
        url
      }
      description
    }
    imageAlignment
    heading
    headingVariant
    backgroundColor
    content {
      content
    }
    contentAsRichText {
      raw
    }
    classname
  }
`

function lightenHexColor(hex, percent) {
  if (percent < 0 || percent > 1) {
    throw new Error("Percent must be a value between 0 and 1")
  }

  // Remove the '#' if present
  if (hex.charAt(0) === "#") {
    hex = hex.slice(1)
  }

  // Convert the hex color to RGB
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)

  // Increase the brightness by the given percentage
  const newR = Math.round(r + (255 - r) * percent)
  const newG = Math.round(g + (255 - g) * percent)
  const newB = Math.round(b + (255 - b) * percent)

  // Convert the new RGB color back to hex
  const newHex = `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB
    .toString(16)
    .padStart(2, "0")}`

  return newHex
}

const GridItem = ({ gridItem, textColor, classname }) => {
  const {
    action,
    image,
    imageAlignment,
    heading,
    content,
    backgroundColor,
    headingVariant,
    contentAsRichText,
    classname: gridItemClassname,
  } = gridItem

  const alignmentMap = {
    left: "flex-start",
    right: "flex-end",
    center: "center",
  }

  const imageAligned = alignmentMap[imageAlignment] || "center"

  return (
    <Flex
      sx={{
        flexDirection: ["column"],
        borderColor: `${backgroundColor} !important`,
        backgroundColor: [
          (classname === "grid-cards" || classname === "grid-cards margin-top-0") &&
            `${lightenHexColor(backgroundColor, 0.8)}`,
        ],
        marginBottom: "2em",
        alignItems: "center",
      }}
      className="grid-item grid-card-item"
    >
      {image && (
        <Flex
          sx={{
            width: "100%",
            mb: "18px",
            mr: ["0"],
            alignItems: ["center", "flex-start"],
            justifyContent: ["center", imageAligned],
          }}
          className="grid-item-card"
        >
          <GatsbyImage image={image.gatsbyImageData} alt={image.description || "Grid item image"} />
        </Flex>
      )}
      <Flex
        sx={{
          flexDirection: "column",
          maxWidth: ["100%", "100%"],
          textAlign: ["center", "left"],
          alignItems: ["center", "flex-start"],
        }}
        className={`grid-item-content ${gridItemClassname}`}
      >
        <Box>
          <Heading as={headingVariant === "h4Bold" ? "h4" : "h5"} variant="bold" mb="21px" sx={{ color: textColor }}>
            {heading}
          </Heading>
          {contentAsRichText && (
            <ContentfulRichText richText={contentAsRichText} textfontSize="16px" fontColor={textColor || "#FAFAFA"} />
          )}
          <Paragraph sx={{ color: textColor || "#FAFAFA !important", fontSize: "16px" }}>{content?.content}</Paragraph>
        </Box>
        {action && <Action {...action} fontsize="17px" secondaryIconcolor={backgroundColor} />}
      </Flex>
    </Flex>
  )
}

export default GridItem
